import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  StarIcon,
  ShoppingBagIcon,
  TruckIcon,
  ShieldCheckIcon,
  PhoneIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import RecentlyViewed from '../components/ui/RecentlyViewed';
import { RecommendedProducts, TrendingProducts } from '../components/recommendations';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import CategoryHero from '../components/categories/CategoryHero';
import { HomepageGallery } from '../components/homepage';
import { 
  PerfumeHero, 
  ProductQuickView, 
  StickyMobileCart, 
  EnhancedProductCard,
  TrustBadges,
  PerfumeFinder
} from '../components/perfume';

const HomePage = () => {
  const { user } = useAuth();
  const { addItem } = useCart();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredCategories, setFeaturedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [showPerfumeFinder, setShowPerfumeFinder] = useState(false);

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get('/api/products/featured/list'),
          axios.get('/api/categories/featured/list')
        ]);
        
        setFeaturedProducts(productsRes.data.products || []);
        setFeaturedCategories(categoriesRes.data.categories || []);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  const features = [
    {
      icon: TruckIcon,
      title: 'משלוח חינם',
      description: 'משלוח חינם על הזמנות מעל ₪200'
    },
    {
      icon: ShieldCheckIcon,
      title: 'אחריות מלאה',
      description: 'אחריות יצרן על כל המוצרים'
    },
    {
      icon: PhoneIcon,
      title: 'שירות 24/7',
      description: 'צוות התמיכה שלנו כאן בשבילכם'
    },
    {
      icon: ShoppingBagIcon,
      title: 'החזרות קלות',
      description: 'החזרת מוצרים תוך 30 יום'
    }
  ];

  const handleQuickView = (product) => {
    setQuickViewProduct(product);
  };

  const handleAddToCart = (product, quantity = 1) => {
    try {
      addItem(product, quantity);
      toast.success(`${product.name} נוסף לעגלה!`);
      setQuickViewProduct(null);
    } catch (error) {
      toast.error('שגיאה בהוספה לעגלה');
    }
  };

  const handleAddToWishlist = async (product) => {
    try {
      await axios.post('/api/wishlist/add', { product_id: product.id });
      setWishlist([...wishlist, product.id]);
      toast.success(`${product.name} נוסף לרשימת המשאלות!`);
    } catch (error) {
      toast.error('שגיאה בהוספה לרשימת משאלות');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-100">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-cream-400 border-t-gold-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Homepage Gallery - Featured Banners */}
      <HomepageGallery />

      {/* Category Hero Section */}
      <CategoryHero />

      {/* Trust Badges Section */}
      <div className="py-16 bg-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-2 tracking-wide">
              למה לבחור בנו
            </h2>
            <div className="w-12 h-px bg-gold-500 mx-auto mb-3" />
            <p className="text-charcoal-800/70 max-w-lg mx-auto text-sm">
              אנחנו מתחייבים לספק לכם חוויית קנייה מעולה עם השירות הטוב ביותר
            </p>
          </div>
          
          <TrustBadges variant="horizontal" />
        </div>
      </div>

      {/* Perfume Finder CTA */}
      <div className="py-20 bg-cream-200 border-y border-cream-400/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SparklesIcon className="h-8 w-8 text-gold-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-charcoal-900 mb-3 tracking-wide">
            מתקשה למצוא את הבושם המושלם?
          </h2>
          <p className="text-charcoal-800/70 mb-10 max-w-xl mx-auto">
            ענה על שאלון קצר ואנחנו נמליץ לך על הבשמים המתאימים ביותר להעדפות שלך
          </p>
          <button
            onClick={() => setShowPerfumeFinder(true)}
            className="inline-flex items-center px-8 py-3.5 bg-charcoal-900 
              text-cream-100 text-base font-medium 
              hover:bg-charcoal-800 transition-colors duration-200 
              border border-charcoal-800"
          >
            <SparklesIcon className="h-5 w-5 ml-2" />
            מצא את הבושם שלך
          </button>
        </div>
      </div>

      {/* Featured Categories */}
      {featuredCategories.length > 0 && (
        <div className="py-16 bg-cream-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-charcoal-900 mb-2 tracking-wide">
                קטגוריות מובילות
              </h2>
              <div className="w-12 h-px bg-gold-500 mx-auto mb-3" />
              <p className="text-charcoal-800/70 max-w-lg mx-auto text-sm">
                גלה את המגוון הרחב של המוצרים שלנו
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCategories.slice(0, 8).map((category) => (
                <Link
                  key={category.id}
                  to={`/categories/${category.slug}`}
                  className="group relative overflow-hidden rounded-sm bg-cream-50 border border-cream-400/60 hover:border-gold-400/50 transition-colors duration-300"
                >
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={category.image || '/placeholder-category.jpg'}
                      alt={category.name}
                      className="w-full h-48 object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold text-charcoal-900 group-hover:text-gold-600 transition-colors">
                      {category.name}
                    </h3>
                    {category.products && category.products.length > 0 && (
                      <p className="text-sm text-charcoal-700/60 mt-0.5">
                        {category.products.length} מוצרים
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link
                to="/products"
                className="inline-flex items-center px-6 py-2.5 border border-charcoal-800 text-charcoal-900 text-sm font-medium hover:bg-charcoal-900 hover:text-cream-100 transition-colors"
              >
                צפה בכל הקטגוריות
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <div className="py-16 bg-cream-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-charcoal-900 mb-2 tracking-wide">
                מוצרים מובילים
              </h2>
              <div className="w-12 h-px bg-gold-500 mx-auto mb-3" />
              <p className="text-charcoal-800/70 max-w-lg mx-auto text-sm">
                המוצרים הנמכרים והמדורגים ביותר שלנו
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.slice(0, 8).map((product) => (
                <EnhancedProductCard
                  key={product.id}
                  product={product}
                  onQuickView={handleQuickView}
                  onAddToCart={(prod) => handleAddToCart(prod, 1)}
                  onAddToWishlist={handleAddToWishlist}
                  isInWishlist={wishlist.includes(product.id)}
                />
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link
                to="/products"
                className="inline-flex items-center px-6 py-2.5 bg-charcoal-900 text-cream-100 text-sm font-medium hover:bg-charcoal-800 transition-colors"
              >
                צפה בכל המוצרים
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Trending Products - Hot Items */}
      <div className="py-16 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TrendingProducts limit={10} days={7} />
        </div>
      </div>

      {/* Personalized Recommendations */}
      {user && (
        <div className="py-16 bg-cream-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RecommendedProducts type="personalized" limit={8} />
          </div>
        </div>
      )}

      {/* Recently Viewed Products */}
      <div className="py-16 bg-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RecentlyViewed maxItems={8} />
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-charcoal-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-cream-100 mb-3 tracking-wide">
            הצטרפו אלינו
          </h2>
          <div className="w-12 h-px bg-gold-500/80 mx-auto mb-4" />
          <p className="text-base text-cream-200/80 mb-10 max-w-lg mx-auto">
            הצטרפו לאלפי לקוחות מרוצים וגלו את החוויה שלנו
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-3 bg-gold-500 text-charcoal-900 font-medium hover:bg-gold-400 transition-colors"
            >
              הרשמה בחינם
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-3 border border-cream-300/40 text-cream-100 hover:bg-cream-100/10 transition-colors"
            >
              צור קשר
            </Link>
          </div>
        </div>
      </div>

      {/* Product Quick View Modal */}
      <ProductQuickView
        product={quickViewProduct}
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleAddToWishlist}
      />

      {/* Sticky Mobile Cart */}
      <StickyMobileCart />

      {/* Perfume Finder Quiz */}
      <PerfumeFinder
        isOpen={showPerfumeFinder}
        onClose={() => setShowPerfumeFinder(false)}
      />
    </div>
  );
};

export default HomePage;
